import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { products } from '../../../mocks/products';
import { useCart } from '../../../hooks/useCart';
import { useProductImages } from '../../../hooks/useProductImages';

export default function FeaturedProducts() {
  const { addItem } = useCart();
  const { getImage, updateImage, getPrice, getName, updateData } = useProductImages();

  const [added, setAdded] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingImageId, setEditingImageId] = useState<string | null>(null);
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [tempPrice, setTempPrice] = useState('');
  const [tempName, setTempName] = useState('');

  const featured = products.slice(0, 4);

  const handleAdd = (product: typeof products[0]) => {
    const price = getPrice(product.id, product.price);
    const name = getName(product.id, product.name);
    const image = getImage(product.id, product.image);
    addItem({ id: product.id, name, price, image });
    setAdded(product.id);
    setTimeout(() => setAdded(null), 1500);
  };

  const handleImageClick = (productId: string) => {
    if (!editMode) return;
    setEditingImageId(productId);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingImageId) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const result = ev.target?.result as string;
      if (result) updateImage(editingImageId, result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
    setEditingImageId(null);
  };

  const showSaved = () => {
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2000);
  };

  const startEditPrice = (productId: string, currentPrice: number) => {
    setEditingPriceId(productId);
    setTempPrice(String(currentPrice));
  };

  const savePrice = (productId: string) => {
    const num = parseFloat(tempPrice);
    if (!isNaN(num) && num > 0) { updateData(productId, { price: num }); showSaved(); }
    setEditingPriceId(null);
  };

  const startEditName = (productId: string, currentName: string) => {
    setEditingNameId(productId);
    setTempName(currentName);
  };

  const saveName = (productId: string) => {
    if (tempName.trim()) { updateData(productId, { name: tempName.trim() }); showSaved(); }
    setEditingNameId(null);
  };

  return (
    <section className="py-24 px-6 lg:px-12 bg-[#0A0A0A] overflow-hidden relative">
      {/* Cinematic background texture */}
      <div className="absolute inset-0 opacity-5 pointer-events-none select-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E8721A] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E8721A] to-transparent" />
      </div>

      {/* Saved Toast */}
      {savedMsg && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-black border border-[#E8721A]/40 text-white text-sm font-semibold px-6 py-3 flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-check-line text-[#E8721A]" />
          </div>
          تم الحفظ بنجاح
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

      <div className="max-w-7xl mx-auto" dir="rtl">
        {/* Header */}
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-px bg-[#E8721A]" />
              <span className="text-[#E8721A] text-xs font-bold tracking-[0.3em] uppercase">منتجاتنا</span>
            </div>
            <h2 className="text-4xl font-black text-white">المنتجات المميزة</h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setEditMode(v => !v)}
              className={`flex items-center gap-2 px-5 py-2.5 text-xs font-bold transition-all cursor-pointer whitespace-nowrap tracking-widest uppercase border ${
                editMode
                  ? 'bg-[#E8721A] text-white border-[#E8721A]'
                  : 'bg-transparent text-white/50 border-white/10 hover:text-[#E8721A] hover:border-[#E8721A]/40'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={editMode ? 'ri-check-double-line' : 'ri-edit-2-line'} />
              </div>
              {editMode ? 'إنهاء التعديل' : 'تعديل المنتجات'}
            </button>
            <Link
              to="/products"
              className="flex items-center gap-2 text-[#E8721A]/70 hover:text-[#E8721A] font-semibold transition-all cursor-pointer whitespace-nowrap text-xs tracking-widest uppercase"
            >
              <span>عرض الكل</span>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line" />
              </div>
            </Link>
          </div>
        </div>

        {/* Edit Mode Hint */}
        {editMode && (
          <div className="border border-[#E8721A]/20 bg-[#E8721A]/5 px-4 py-3 mb-8 flex items-center justify-center gap-6 text-xs text-[#E8721A]/70 font-medium flex-wrap">
            <span className="flex items-center gap-2"><div className="w-4 h-4 flex items-center justify-center"><i className="ri-camera-line" /></div>اضغط الصورة لتغييرها</span>
            <span className="text-[#E8721A]/30">|</span>
            <span className="flex items-center gap-2"><div className="w-4 h-4 flex items-center justify-center"><i className="ri-price-tag-3-line" /></div>اضغط السعر لتعديله</span>
            <span className="text-[#E8721A]/30">|</span>
            <span className="flex items-center gap-2"><div className="w-4 h-4 flex items-center justify-center"><i className="ri-text" /></div>اضغط الاسم لتعديله</span>
          </div>
        )}

        {/* Products Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-product-shop>
          {featured.map((product, idx) => {
            const displayImage = getImage(product.id, product.image);
            const displayPrice = getPrice(product.id, product.price);
            const displayName = getName(product.id, product.name);

            return (
              <div
                key={product.id}
                className={`group relative bg-[#111] overflow-hidden transition-all duration-500 hover:-translate-y-2 ${
                  editMode ? 'border border-[#E8721A]/30' : 'border border-white/5 hover:border-[#E8721A]/30'
                }`}
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                {/* Image */}
                <div
                  className={`relative w-full h-72 bg-[#0D0D0D] overflow-hidden ${editMode ? 'cursor-pointer' : ''}`}
                  onClick={() => handleImageClick(product.id)}
                >
                  <img
                    src={displayImage}
                    alt={displayName}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />
                  {/* Cinematic overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {editMode && (
                    <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-12 h-12 bg-[#E8721A] flex items-center justify-center mb-2">
                        <i className="ri-camera-line text-white text-xl" />
                      </div>
                      <span className="text-white text-xs font-bold tracking-widest uppercase">تغيير الصورة</span>
                    </div>
                  )}
                  {product.badge && (
                    <span className="absolute top-3 right-3 bg-[#E8721A] text-white text-[10px] font-bold px-3 py-1 tracking-widest uppercase">
                      {product.badge}
                    </span>
                  )}
                  <button
                    className="absolute top-3 left-3 w-8 h-8 flex items-center justify-center bg-black/50 hover:bg-[#E8721A] transition-colors cursor-pointer"
                    aria-label="إضافة للمفضلة"
                    onClick={e => e.stopPropagation()}
                  >
                    <i className="ri-heart-line text-white text-sm" />
                  </button>
                </div>

                {/* Info */}
                <div className="p-4">
                  {/* Editable Name */}
                  {editMode && editingNameId === product.id ? (
                    <input
                      autoFocus
                      value={tempName}
                      onChange={e => setTempName(e.target.value)}
                      onBlur={() => saveName(product.id)}
                      onKeyDown={e => { if (e.key === 'Enter') saveName(product.id); if (e.key === 'Escape') setEditingNameId(null); }}
                      className="w-full border-b border-[#E8721A] bg-transparent text-white font-bold text-sm mb-1 focus:outline-none text-right"
                    />
                  ) : (
                    <h3
                      className={`text-white font-bold text-sm mb-1 truncate ${editMode ? 'cursor-pointer hover:text-[#E8721A] transition-colors' : ''}`}
                      onClick={() => editMode && startEditName(product.id, displayName)}
                    >
                      {displayName}
                      {editMode && (
                        <span className="inline-flex items-center justify-center w-3 h-3 mr-1">
                          <i className="ri-edit-line text-[#E8721A] text-xs" />
                        </span>
                      )}
                    </h3>
                  )}

                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="w-3 h-3 flex items-center justify-center">
                        <i className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-[#E8721A] text-xs`} />
                      </div>
                    ))}
                    <span className="text-white/30 text-xs mr-1">({product.reviews})</span>
                  </div>

                  {/* Editable Price */}
                  <div className="flex items-center gap-2 mb-4">
                    {editMode && editingPriceId === product.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          autoFocus
                          type="number"
                          value={tempPrice}
                          onChange={e => setTempPrice(e.target.value)}
                          onBlur={() => savePrice(product.id)}
                          onKeyDown={e => { if (e.key === 'Enter') savePrice(product.id); if (e.key === 'Escape') setEditingPriceId(null); }}
                          className="w-24 border-b border-[#E8721A] bg-transparent text-[#E8721A] font-black text-xl focus:outline-none text-right"
                        />
                        <span className="text-white/40 text-xs font-medium">د.إ</span>
                      </div>
                    ) : (
                      <div
                        className={`flex items-center gap-1 ${editMode ? 'cursor-pointer' : ''}`}
                        onClick={() => editMode && startEditPrice(product.id, displayPrice)}
                      >
                        <span className="text-[#E8721A] font-black text-xl">{displayPrice}</span>
                        <span className="text-white/40 text-xs font-medium">د.إ</span>
                        {editMode && (
                          <span className="flex items-center justify-center w-4 h-4">
                            <i className="ri-edit-line text-[#E8721A] text-xs opacity-70" />
                          </span>
                        )}
                      </div>
                    )}
                    {product.oldPrice && !editMode && (
                      <span className="text-white/20 text-xs line-through">{product.oldPrice}</span>
                    )}
                  </div>

                  <button
                    onClick={() => !editMode && handleAdd(product)}
                    className={`w-full py-3 text-xs font-bold transition-all duration-300 whitespace-nowrap tracking-widest uppercase ${
                      editMode
                        ? 'bg-white/5 text-white/20 cursor-not-allowed'
                        : added === product.id
                        ? 'bg-green-600 text-white cursor-pointer'
                        : 'bg-[#E8721A] text-white hover:bg-[#C05A0A] cursor-pointer'
                    }`}
                  >
                    {editMode ? 'وضع التعديل' : added === product.id ? '✓ تمت الإضافة' : 'أضف للسلة'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
